import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  LEGAL_AID_LIST_QUERY_KEY,
  LEGAL_AID_QUERY_KEY,
  createLegalAid,
  createLegalAidDetailQueryKey,
  createLegalAidFileQueryKey,
  deleteLegalAid,
  fetchLegalAidById,
  fetchLegalAidDocumentFile,
  fetchLegalAids,
  fetchLegalAidsForInjunction,
  fetchLegalAidsForImposition,
  fetchLegalAidsForReporting,
  type CreateLegalAidRequest,
} from "@/api/legalAid";
import { invalidateLegalAidQueries } from "@/features/cle-compliance/hooks/invalidateCleDocumentQueries";
import type { PaginatedResponse } from "@/features/cle-compliance/models/courseCertificate";
import type {
  LegalAidEvidenceListModel,
  LegalAidEvidenceModel,
  LegalAidQueryParams,
} from "@/features/cle-compliance/models/legalAid";

export {
  LEGAL_AID_LIST_QUERY_KEY,
  LEGAL_AID_QUERY_KEY,
  createLegalAidDetailQueryKey,
  createLegalAidFileQueryKey,
} from "@/api/legalAid";

export type { CreateLegalAidRequest } from "@/api/legalAid";

export const useLegalAids = (params: LegalAidQueryParams = {}) =>
  useQuery<PaginatedResponse<LegalAidEvidenceListModel>>({
    queryKey: [...LEGAL_AID_LIST_QUERY_KEY, params],
    queryFn: () => fetchLegalAids(params),
    staleTime: 5 * 60 * 1000,
  });

export const useLegalAidsForReporting = (reportId: string) =>
  useQuery<LegalAidEvidenceListModel[]>({
    queryKey: [...LEGAL_AID_QUERY_KEY, "for-reporting", reportId],
    queryFn: () => fetchLegalAidsForReporting(reportId),
    staleTime: 5 * 60 * 1000,
  });

export const useLegalAidsForInjunction = (
  injunctionId: string,
  enabled = true,
) =>
  useQuery<LegalAidEvidenceListModel[]>({
    enabled: enabled && injunctionId.length > 0,
    queryKey: [...LEGAL_AID_QUERY_KEY, "for-injunction", injunctionId],
    queryFn: () => fetchLegalAidsForInjunction(injunctionId),
    staleTime: 5 * 60 * 1000,
  });

export const useLegalAidsForImposition = (
  impositionId: string,
  enabled = true,
) =>
  useQuery<LegalAidEvidenceListModel[]>({
    enabled: enabled && impositionId.length > 0,
    queryKey: [...LEGAL_AID_QUERY_KEY, "for-imposition", impositionId],
    queryFn: () => fetchLegalAidsForImposition(impositionId),
    staleTime: 5 * 60 * 1000,
  });

export const useLegalAidDocument = (id: string) =>
  useQuery<LegalAidEvidenceModel>({
    enabled: id.length > 0,
    queryKey: createLegalAidDetailQueryKey(id),
    queryFn: () => fetchLegalAidById(id),
    staleTime: 5 * 60 * 1000,
  });

export const useLegalAidDocumentFile = (id: string) =>
  useQuery<Blob>({
    enabled: id.length > 0,
    queryKey: createLegalAidFileQueryKey(id),
    queryFn: () => fetchLegalAidDocumentFile(id),
    staleTime: 5 * 60 * 1000,
  });

export const useCreateLegalAid = () => {
  const queryClient = useQueryClient();

  return useMutation<LegalAidEvidenceModel, Error, CreateLegalAidRequest>({
    mutationFn: createLegalAid,
    onSuccess: () => {
      invalidateLegalAidQueries(queryClient);
    },
  });
};

export const useDeleteLegalAid = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: deleteLegalAid,
    onSuccess: (_data, id) => {
      invalidateLegalAidQueries(queryClient, id);
    },
  });
};
